import React, { ForwardedRef, RefObject } from 'react';
import clsx from 'clsx';
import { FocusRingAria } from '@react-aria/focus';
import {
  setInteractionModality,
  useFocusWithin,
  useHover,
} from '@react-aria/interactions';
import { mergeProps, useObjectRef, filterDOMProps } from '@react-aria/utils';
import { SelectState } from '@react-stately/select';
import { ComboBoxState } from '@react-stately/combobox';
import { ValidationResult } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/select/select.module.css';
import { ChevronDown } from '@danske/sapphire-icons/react';

import { SelectProps } from './SelectProps';
import { MultiSelectProps } from './MultiSelect';
import { MultiSelectState } from './useMultiSelectState';
import { Field } from '../../Field';
import { Icon } from '../../Icon';
import { Label } from '../../Label';
import {
  ListBoxPopover,
  useButton,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { MultiSelectAria } from './useMultiSelect';

/**
 * Common parts of Select and SearchableSelect, which is mostly about rendering.
 * The reason SearchableSelect is a separate component, in the first place, is
 * that it requires a different set of hooks for the logic and state management.
 * If conditionally calling hooks was not an issue in react, we could have added
 * a `searchable` as an option on `Select`.
 */

export function SelectBase<
  T extends object,
  M extends 'single' | 'multiple' = 'single'
>({
  selectProps: {
    isDisabled,
    loadingState,
    loadingSkeletonRowsCount,
    emptyState,
    error,
    label,
    children,
    note,
    contextualHelp,
    necessityIndicator = false,
    isRequired,
    prefix,
    ...otherProps
  },
  selectAria: {
    valueProps,
    descriptionProps,
    errorMessageProps,
    labelProps,
    menuProps,
    triggerProps,
  },
  focusRingAria: { isFocusVisible, focusProps },
  state,
  containerRef,
  popoverRef,
  listBoxRef,
  triggerRef,
  selectValueContent,
  isNonModal,
  size = 'lg',
  labelPlacement = 'above',
}: {
  containerRef: ForwardedRef<HTMLDivElement>;
  popoverRef: RefObject<HTMLDivElement>;
  listBoxRef: RefObject<HTMLUListElement>;
  triggerRef: RefObject<HTMLDivElement>;
  selectValueContent: React.ReactNode;
  selectAria: Omit<MultiSelectAria<T>, keyof ValidationResult>;
  focusRingAria: FocusRingAria;
  isNonModal?: boolean;
  /**
   * @default "lg"
   */
  size?: 'lg' | 'md';
  /**
   * Places the label either above (default) or on the side of the control.
   * @default 'above'
   */
  labelPlacement?: 'side' | 'above';
} & (
  | {
      state: SelectState<T, M> | ComboBoxState<T>;
      selectProps: SelectProps<T, M>;
    }
  | {
      /**
       * Even though we support `selectionMode="multiple"` for regular Select,
       * we need this branch for the other multiple select components.
       */
      multiple: true;
      state: MultiSelectState<T>;
      selectProps: MultiSelectProps<T>;
    }
)): JSX.Element {
  useThemeCheck();

  const ref = useObjectRef<HTMLDivElement>(containerRef);
  const { styleProps } = useSapphireStyleProps(otherProps);
  const { hoverProps } = useHover({ isDisabled });

  const { onBlur, onFocus, onFocusChange, ...unfocusableTriggerProps } =
    triggerProps;

  const { focusWithinProps } = useFocusWithin({
    onBlurWithin: onBlur,
    onFocusWithin: onFocus,
    onFocusWithinChange: onFocusChange,
  });

  const { buttonProps } = useButton(
    {
      ...unfocusableTriggerProps,
      elementType: 'div',
      excludeFromTabOrder: false, // triggerProps returned from useComboBox sets it to true
    },
    triggerRef
  );

  return (
    <Field
      ref={ref}
      size={size}
      UNSAFE_style={styleProps.style}
      UNSAFE_className={styleProps.className}
      labelPlacement={labelPlacement}
    >
      <Field.Context
        descriptionProps={error ? errorMessageProps : descriptionProps}
      >
        {label && (
          <Field.Label>
            <Label
              {...mergeProps(labelProps, {
                onClick: () => {
                  if (isDisabled || !triggerRef.current) {
                    return;
                  }

                  Promise.resolve().then(() => {
                    setInteractionModality('keyboard');
                    triggerRef.current?.focus();
                  });
                },
              })}
              size={size}
              necessityIndicator={
                isRequired && necessityIndicator
                  ? 'required'
                  : !isRequired && necessityIndicator
                  ? 'optional'
                  : undefined
              }
              contextualHelp={contextualHelp}
            >
              {label}
            </Label>
          </Field.Label>
        )}
        <Field.Control>
          <div
            className={clsx(styles['sapphire-select'], {
              [styles['is-focus']]: isFocusVisible,
              [styles['is-disabled']]: isDisabled,
              [styles['sapphire-select--md']]: size === 'md',
              [styles['sapphire-select--error']]: error,
              [styles['sapphire-select--open']]: state.isOpen,
            })}
          >
            <div
              {...mergeProps(
                buttonProps,
                focusWithinProps,
                focusProps,
                hoverProps
              )}
              {...filterDOMProps(otherProps, { global: true })}
              ref={triggerRef}
              className={clsx(styles['sapphire-select__button'])}
            >
              {prefix && (
                <span className={styles['sapphire-select__prefix']}>
                  {prefix}
                </span>
              )}
              <div {...valueProps} className={styles['sapphire-select__value']}>
                {selectValueContent}
              </div>
              <div className={clsx(styles['sapphire-select__icon-container'])}>
                <Icon size={size === 'md' ? 'sm' : 'md'}>
                  <ChevronDown />
                </Icon>
              </div>
            </div>
          </div>
          {state.isOpen && (
            <ListBoxPopover<T>
              state={state}
              triggerRef={triggerRef}
              popoverRef={popoverRef}
              listBoxRef={listBoxRef}
              listBoxProps={menuProps}
              selectWidth={triggerRef.current?.offsetWidth}
              loadingState={loadingState}
              loadingSkeletonRowsCount={loadingSkeletonRowsCount}
              emptyState={emptyState}
              isNonModal={isNonModal}
              size={size}
            >
              {children}
            </ListBoxPopover>
          )}
        </Field.Control>
        {!!error && typeof error !== 'boolean' ? (
          <Field.Footer>
            <Field.Note variant="error">{error}</Field.Note>
          </Field.Footer>
        ) : note ? (
          <Field.Footer>{note}</Field.Footer>
        ) : (
          <></>
        )}
      </Field.Context>
    </Field>
  );
}
